import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import Button from "../../Buttons/Button";
import { useTranslationApp } from "../../../lib/i18next";
import {
  AUTOMATIONS_BUTTON_BACK,
  AUTOMATIONS_STEP_PLATFORMS,
  AUTOMATIONS_STEP_REVIEW,
  AUTOMATIONS_STEP_SCHEDULE,
  AUTOMATIONS_STEP_SOURCE,
} from "../../../i18n/keysTranslations";
import {
  STEPPER_BACKGROUND,
  STEPPER_STEP_DONE_CIRCLE,
  STEPPER_STEP_DONE_TEXT,
  STEPPER_STEP_PENDING_CIRCLE,
  subtleDividerSx,
} from "./automationModalStyles";

const STEP_KEYS = [
  AUTOMATIONS_STEP_SOURCE,
  AUTOMATIONS_STEP_PLATFORMS,
  AUTOMATIONS_STEP_SCHEDULE,
  AUTOMATIONS_STEP_REVIEW,
];

const StepCircle = ({ stepNumber, isCurrent, isDone, size = 28 }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      minWidth: size,
      borderRadius: "50%",
      flexShrink: 0,
      backgroundColor: isCurrent
        ? "primary.main"
        : isDone
          ? STEPPER_STEP_DONE_CIRCLE
          : STEPPER_STEP_PENDING_CIRCLE,
      color: isCurrent
        ? "primary.contrastText"
        : isDone
          ? STEPPER_STEP_DONE_TEXT
          : "text.secondary",
    }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {stepNumber}
    </Typography>
  </Box>
);

const VerticalStepper = ({
  currentStep,
  onStepClick,
  onBack,
  isBackDisabled,
  t,
}) => (
  <Box
    sx={{
      backgroundColor: STEPPER_BACKGROUND,
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
      borderRight: `1px solid`,
      ...subtleDividerSx,
    }}
  >
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        py: 3,
        px: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      {STEP_KEYS.map((stepKey, index) => {
        const stepNumber = index + 1;
        const isCurrent = stepNumber === currentStep;
        const isDone = stepNumber < currentStep;

        return (
          <ButtonBase
            key={stepKey}
            onClick={() => onStepClick(stepNumber)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              justifyContent: "flex-start",
              width: "100%",
              textAlign: "left",
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              color: isCurrent
                ? "background.paperContrastText"
                : "text.secondary",
              fontWeight: isCurrent ? 600 : 400,
            }}
          >
            <StepCircle
              stepNumber={stepNumber}
              isCurrent={isCurrent}
              isDone={isDone}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: "inherit",
                minWidth: 0,
                overflowWrap: "anywhere",
              }}
            >
              {t(stepKey)}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>

    <Box
      sx={{
        flexShrink: 0,
        px: 2,
        py: 2,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Button
        color="secondary"
        onClick={onBack}
        disabled={isBackDisabled}
        sx={{ width: "auto" }}
      >
        {t(AUTOMATIONS_BUTTON_BACK)}
      </Button>
    </Box>
  </Box>
);

const HORIZONTAL_CIRCLE_SIZE = 32;

const HorizontalStepper = ({ currentStep, onStepClick, t }) => {
  const lastIndex = STEP_KEYS.length - 1;
  const progress = lastIndex === 0 ? 0 : (currentStep - 1) / lastIndex;

  return (
    <Box
      sx={{
        backgroundColor: STEPPER_BACKGROUND,
        flexShrink: 0,
        px: { xs: 2, sm: 3 },
        pt: 1.5,
        pb: 4.5,
        borderBottom: `1px solid`,
        ...subtleDividerSx,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: HORIZONTAL_CIRCLE_SIZE / 2 - 1,
            left: HORIZONTAL_CIRCLE_SIZE / 2,
            right: HORIZONTAL_CIRCLE_SIZE / 2,
            height: 2,
            borderRadius: 1,
            bgcolor: STEPPER_STEP_PENDING_CIRCLE,
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: HORIZONTAL_CIRCLE_SIZE / 2 - 1,
            left: HORIZONTAL_CIRCLE_SIZE / 2,
            width: `calc((100% - ${HORIZONTAL_CIRCLE_SIZE}px) * ${progress})`,
            height: 2,
            borderRadius: 1,
            bgcolor: "primary.main",
            zIndex: 0,
          }}
        />
        {STEP_KEYS.map((stepKey, index) => {
          const stepNumber = index + 1;
          const isCurrent = stepNumber === currentStep;
          const isDone = stepNumber < currentStep;
          const stepColor = isCurrent
            ? "background.paperContrastText"
            : "text.secondary";

          return (
            <ButtonBase
              key={stepKey}
              onClick={() => onStepClick(stepNumber)}
              aria-current={isCurrent ? "step" : undefined}
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: HORIZONTAL_CIRCLE_SIZE,
                minWidth: HORIZONTAL_CIRCLE_SIZE,
                flex: "0 0 auto",
                px: 0,
                py: 0,
                borderRadius: 1,
                color: stepColor,
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              <StepCircle
                stepNumber={stepNumber}
                isCurrent={isCurrent}
                isDone={isDone}
                size={HORIZONTAL_CIRCLE_SIZE}
              />
              <Typography
                variant="body2"
                sx={{
                  position: "absolute",
                  top: HORIZONTAL_CIRCLE_SIZE + 4,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "max-content",
                  maxWidth: 80,
                  fontWeight: "inherit",
                  color: "inherit",
                  textAlign: "center",
                  lineHeight: 1.2,
                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                  overflowWrap: "anywhere",
                }}
              >
                {t(stepKey)}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

const AutomationStepper = ({
  currentStep,
  onStepClick,
  onBack,
  isBackDisabled = false,
  variant = "vertical",
}) => {
  const { t } = useTranslationApp();

  switch (variant) {
    case "horizontal":
      return (
        <HorizontalStepper
          currentStep={currentStep}
          onStepClick={onStepClick}
          t={t}
        />
      );
    case "vertical":
      return (
        <VerticalStepper
          currentStep={currentStep}
          onStepClick={onStepClick}
          onBack={onBack}
          isBackDisabled={isBackDisabled}
          t={t}
        />
      );
    default: {
      const _exhaustive = variant;
      void _exhaustive;
      return (
        <VerticalStepper
          currentStep={currentStep}
          onStepClick={onStepClick}
          onBack={onBack}
          isBackDisabled={isBackDisabled}
          t={t}
        />
      );
    }
  }
};

export default AutomationStepper;
